// The dashboard's filter: which organizations, repositories, authors,
// waited-on people's and labels' pull requests show. Pure, and shared with the browser
// extension: both pass the pull requests they have loaded (as `Subject`s)
// and render what comes back, so filtering asks GitHub for nothing.
//
// A filter holds the values chosen in each facet. A pull request matches
// when, in every facet with values, it has one of them: any organization
// chosen AND any author chosen, and so on. Names compare without regard to
// case, as GitHub's do; a filter holds them lowercased.
//
// Labels follow the same rule: several chosen show the pull requests that
// have ANY of them. A label is its name: `bug` in two repositories is one
// value. `NO_LABEL` stands for having none. Only the labels a row carries
// are seen (the first LABELS_PER_PR of a pull request, see api/queries.ts).
//
// The filter is one per user, kept on the server (the preference
// `FILTER_PREFERENCE`, with a revision that grows with every write), so
// the web app in every browser and the extension show the same one. Each
// client keeps a copy (`FILTER_KEY` in the web app, deleted with the
// user's other local state) to show before the server answers, with the
// revision it is of, marked `unsent` while it holds a change the server
// has not confirmed; `reconcile` says what the server's answer makes of
// it. Only the user's own choices, made with the controls, change it.
//
// The dashboard's URL shows the filter in view
// (`?org=a,b&repo=a/web&author=x&waiting=y&label=bug&label=help%20wanted`:
// one `label` per name, since a label's name may hold a comma). A URL that names another
// filter than the user's shows it for that view alone (`viewOf`): opening
// a link never changes anything stored.

export const FACETS = ['org', 'repo', 'author', 'waiting', 'label'] as const;
export type Facet = (typeof FACETS)[number];

/** Each facet's name, as the filter bar and its chips say it. */
export const FACET_LABEL: Record<Facet, string> = { org: 'Org', repo: 'Repo', author: 'Author', waiting: 'Waiting on', label: 'Label' };

/**
 * The Label facet's value for a pull request with no labels. GitHub's own
 * search words it so (`no:label`), and no sensible label is named that.
 */
export const NO_LABEL = 'no:label';
const NO_LABEL_NAME = 'No labels';

const each = <T>(of: (facet: Facet) => T): Record<Facet, T> => Object.fromEntries(FACETS.map((f) => [f, of(f)])) as Record<Facet, T>;

/** The values chosen in each facet: lowercased, once each, sorted. No values in a facet means any. */
export type DashFilter = Readonly<Record<Facet, readonly string[]>>;

export const NO_FILTER: DashFilter = Object.freeze(each((): readonly string[] => []));

/** What the filter reads of a pull request. */
export interface Subject {
  /** Its identity: a pull request listed twice counts once. */
  id: string;
  /** `owner/name`; its organization (or owning account) is the owner. */
  repo: string;
  /** Its author's login; null when GitHub names none, or it is not known. */
  author: string | null;
  /** Who it is waiting on (its attention set); null while that is not known. */
  waiting: readonly string[] | null;
  /** Its labels' names; absent when they were not read (a row kept from before they were), which counts as none. */
  labels?: readonly string[];
}

const key = (v: string) => v.trim().toLowerCase();
const ownerOf = (repo: string) => repo.split('/')[0];

/** The filter holding `values`, as a `DashFilter` is kept. */
export function normalize(values: Partial<Record<Facet, readonly string[]>>): DashFilter {
  const facet = (f: Facet) => [...new Set((values[f] ?? []).map(key).filter(Boolean))].sort();
  return each(facet);
}

/** How many values are chosen, over every facet. */
export const activeCount = (f: DashFilter): number => FACETS.reduce((n, facet) => n + f[facet].length, 0);

/** Whether anything is chosen. */
export const isActive = (f: DashFilter): boolean => activeCount(f) > 0;

/** `f` with `value` chosen in `facet`, or no longer chosen when it was. */
export function toggle(f: DashFilter, facet: Facet, value: string): DashFilter {
  const v = key(value);
  return normalize({ ...f, [facet]: f[facet].includes(v) ? f[facet].filter((x) => x !== v) : [...f[facet], v] });
}

/** `f` with nothing chosen in `facet`. */
export const clearFacet = (f: DashFilter, facet: Facet): DashFilter => ({ ...f, [facet]: [] });

/** A pull request's values in `facet`, lowercased; none when they are not known. */
function valuesOf(s: Subject, facet: Facet): string[] {
  switch (facet) {
    case 'org':
      return [key(ownerOf(s.repo))];
    case 'repo':
      return [key(s.repo)];
    case 'author':
      return s.author ? [key(s.author)] : [];
    case 'waiting':
      return (s.waiting ?? []).map(key);
    case 'label':
      return s.labels?.length ? s.labels.map(key) : [NO_LABEL];
  }
}

/** A pull request's values in `facet`, as GitHub spells them. */
function namesOf(s: Subject, facet: Facet): string[] {
  switch (facet) {
    case 'org':
      return [ownerOf(s.repo)];
    case 'repo':
      return [s.repo];
    case 'author':
      return s.author ? [s.author] : [];
    case 'waiting':
      return [...(s.waiting ?? [])];
    case 'label':
      return s.labels?.length ? [...s.labels] : [NO_LABEL];
  }
}

const matchesFacet = (f: DashFilter, s: Subject, facet: Facet) =>
  !f[facet].length || (facet === 'waiting' && s.waiting === null) || valuesOf(s, facet).some((v) => f[facet].includes(v));

/** Whether `s` passes `f`, leaving `except`'s choices out. */
const matchesBut = (f: DashFilter, s: Subject, except: Facet | null) => FACETS.every((facet) => facet === except || matchesFacet(f, s, facet));

/**
 * Whether the pull request shows under `f`: in every facet with values, it
 * has one of them. An author that is not known matches no value. A wait
 * that is not known (its attention could not be worked out) passes the
 * Waiting-on facet: the pull request may be waiting on the very person
 * chosen, so it stays in view, marked (`waitUnknown`), rather than vanish.
 */
export const matches = (f: DashFilter, s: Subject): boolean => matchesBut(f, s, null);

/** Whether `s` shows under `f` only because who it waits on is not known. */
export const waitUnknown = (f: DashFilter, s: Subject): boolean => f.waiting.length > 0 && s.waiting === null && matches(f, s);

/** One choice a facet's picker offers. */
export interface FacetOption {
  /** The value a filter holds (lowercased). */
  value: string;
  /** The name as GitHub spells it, the viewer's marked `(you)`. */
  label: string;
  /** How many loaded pull requests have it, among those the other facets' choices leave. */
  count: number;
  selected: boolean;
}

/**
 * What each facet's picker offers: every value the loaded pull requests
 * have, with how many have it (each pull request once, however many
 * sections list it), most first and then by name. A facet's counts are
 * taken under the other facets' choices and not its own, so a count is what
 * choosing that value would show. A chosen value no loaded pull request has
 * stays listed, counting none, so it can be taken back.
 */
export function facetOptions(subjects: readonly Subject[], f: DashFilter, me = ''): Record<Facet, FacetOption[]> {
  const once = [...new Map(subjects.map((s) => [s.id, s])).values()];
  const mine = key(me);
  const options = (facet: Facet): FacetOption[] => {
    const found = new Map<string, { label: string; count: number }>();
    for (const s of once) {
      const names = namesOf(s, facet);
      const counted = matchesBut(f, s, facet);
      for (const name of new Map(names.map((n) => [key(n), n])).values()) {
        const at = found.get(key(name)) ?? { label: name, count: 0 };
        if (counted) at.count++;
        found.set(key(name), at);
      }
    }
    for (const v of f[facet]) if (!found.has(v)) found.set(v, { label: v, count: 0 });
    return [...found]
      .filter(([value, o]) => o.count > 0 || f[facet].includes(value))
      .map(([value, o]) => ({
        value,
        label: facet === 'label' && value === NO_LABEL ? NO_LABEL_NAME : mine && value === mine && (facet === 'author' || facet === 'waiting') ? `${o.label} (you)` : o.label,
        count: o.count,
        selected: f[facet].includes(value),
      }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  };
  return each(options);
}

/** A chosen value, as the chip that takes it back. */
export interface Chip {
  facet: Facet;
  value: string;
  label: string;
  /** `Org: acme`. */
  text: string;
}

/** The chosen values, facet by facet, each labelled as `options` spells it. */
export function chipsOf(f: DashFilter, options: Record<Facet, readonly FacetOption[]>): Chip[] {
  return FACETS.flatMap((facet) =>
    f[facet].map((value) => {
      const label = options[facet].find((o) => o.value === value)?.label ?? value;
      return { facet, value, label, text: `${FACET_LABEL[facet]}: ${label}` };
    }),
  );
}

/** A section's rows under a filter. */
export interface Filtered<R> {
  /** The rows that match, in their order. */
  rows: readonly R[];
  /** How many rows the section has, matching or not. */
  total: number;
  /** IDs of the rows the filter leaves out: in a stack that still shows, they are its context rows. */
  out: ReadonlySet<string>;
}

const NONE: ReadonlySet<string> = new Set();

/**
 * `rows` under `f`. Stacks follow from it: a section lays out the matching
 * rows over its whole pool (`stackSection`, told what the filter leaves
 * out by `leftOut`), so a stack shows when any of its section's members
 * match, and the members left out keep their places as thin context rows
 * that its summary does not count.
 */
export function filterRows<R>(rows: readonly R[], f: DashFilter, subject: (row: R) => Subject): Filtered<R> {
  if (!isActive(f)) return { rows, total: rows.length, out: NONE };
  const out = new Set<string>();
  const kept = rows.filter((r) => {
    const s = subject(r);
    if (matches(f, s)) return true;
    out.add(s.id);
    return false;
  });
  return { rows: kept, total: rows.length, out };
}

/**
 * Whether `f` leaves a row out, for `stackSection`: a stack's members it
 * leaves out are context rows its summary does not count.
 */
export const leftOut =
  <R>(f: DashFilter, subject: (row: R) => Subject) =>
  (row: R): boolean =>
    isActive(f) && !matches(f, subject(row));

/** A section's count: how many show, "N of M" while a filter is on. */
export const countText = (shown: number, total: number, filtered: boolean): string => (filtered ? `${shown} of ${total}` : String(total));

/** The part of a dashboard row the filter reads. */
export interface FilterRow {
  pr: { id: string; repository: { nameWithOwner: string }; author?: { login: string } | null; labels?: { nodes: readonly { name: string }[] } };
  attention: readonly { login: string }[] | null;
}

/** A dashboard row as the filter sees it: who it waits on is its attention set. */
export function rowSubject(row: FilterRow): Subject {
  return {
    id: row.pr.id,
    repo: row.pr.repository.nameWithOwner,
    author: row.pr.author?.login ?? null,
    waiting: row.attention && row.attention.map((m) => m.login),
    labels: row.pr.labels?.nodes.map((l) => l.name),
  };
}

// ---------------------------------------------------------------- the URL

/** A value in the query string: readable where it can be (`acme/web` keeps its slash). */
const encode = (v: string) => encodeURIComponent(v).replace(/%2F/gi, '/');

/**
 * The filter a query string names (`org`, `repo`, `author`, `waiting`,
 * each a comma-separated list, or repeated), or null when it names none of
 * them: the dashboard then opens on the remembered one.
 */
export function parseFilterQuery(q: URLSearchParams): DashFilter | null {
  if (!FACETS.some((f) => q.has(f))) return null;
  // A label's name may hold a comma: each has a parameter of its own.
  const values = (f: Facet) => (f === 'label' ? q.getAll(f) : q.getAll(f).flatMap((v) => v.split(',')));
  return normalize(each(values));
}

/** `search` (with its `?`, or empty) naming `f` instead of whatever filter it named; its other parameters stay as written. */
export function filterSearch(search: string, f: DashFilter): string {
  const others = search
    .replace(/^\?/, '')
    .split('&')
    .filter((part) => part && !FACETS.includes(decodeName(part.split('=')[0]) as Facet));
  const own = FACETS.filter((facet) => f[facet].length).flatMap((facet) =>
    facet === 'label' ? f[facet].map((v) => `${facet}=${encodeURIComponent(v)}`) : [`${facet}=${f[facet].map(encode).join(',')}`],
  );
  const all = [...others, ...own];
  return all.length ? `?${all.join('&')}` : '';
}

function decodeName(name: string): string {
  try {
    return decodeURIComponent(name.replace(/\+/g, ' '));
  } catch {
    return name;
  }
}

// ---------------------------------------------------------------- this browser

/** localStorage key of the remembered filter (per user: deleted at sign-out, and when another user signs in). */
export const FILTER_KEY = 'changelist:dashboard-filter';

/** The filter stored as `v`; whatever of it is not a list of names is left out. */
export function parseStored(v: unknown): DashFilter {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return NO_FILTER;
  const names = (x: unknown) => (Array.isArray(x) ? x.filter((n): n is string => typeof n === 'string') : []);
  const o = v as Record<string, unknown>;
  return normalize(each((f) => names(o[f])));
}

/** The name of the server preference that holds the filter (`/api/me/preferences`). */
export const FILTER_PREFERENCE = 'dashboard_filter';

/** A client's copy of the shared filter. */
export interface CachedFilter {
  filter: DashFilter;
  /** The server's revision the copy is of (or was changed from); 0 when the server never had one. */
  rev: number;
  /** It holds a change the server has not confirmed. */
  unsent: boolean;
  /** When that change was made (epoch ms); 0 when nothing is unsent. */
  at: number;
  /**
   * The filter the unsent change was made from (the server's, at `rev`),
   * so the change can be made again on a later revision (`rebase`); null
   * when nothing is unsent, or it is not known.
   */
  base: DashFilter | null;
}

const NO_COPY: CachedFilter = { filter: NO_FILTER, rev: 0, unsent: false, at: 0, base: null };

const count = (v: unknown): number => (typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : 0);

/** The copy stored as `v` (`parseStored`, with its revision and `unsent` mark). */
export function parseCached(v: unknown): CachedFilter {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return NO_COPY;
  const o = v as Record<string, unknown>;
  const unsent = o.unsent === true;
  const base = unsent && o.base && typeof o.base === 'object' ? parseStored(o.base) : null;
  return { filter: parseStored(v), rev: count(o.rev), unsent, at: unsent ? count(o.at) : 0, base };
}

/** A copy as it is stored: the filter's facets, its revision, and the mark and time of an unsent change. */
export function cachedValue(c: CachedFilter): Record<string, unknown> {
  return { ...c.filter, ...(c.rev ? { rev: c.rev } : {}), ...(c.unsent ? { unsent: true, at: c.at, ...(c.base ? { base: c.base } : {}) } : {}) };
}

/** Whether a copy holds anything worth storing: no filter, sent, that the server never had is nothing. */
export const worthKeeping = (c: CachedFilter): boolean => isActive(c.filter) || c.unsent || c.rev > 0;

/** This browser's copy of the filter; none when there is none or it cannot be read. */
export function loadCached(): CachedFilter {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    return raw === null ? NO_COPY : parseCached(JSON.parse(raw));
  } catch {
    return NO_COPY;
  }
}

/** Store this browser's copy (a convenience: a storage failure is ignored). */
export function saveCached(c: CachedFilter) {
  try {
    if (worthKeeping(c)) localStorage.setItem(FILTER_KEY, JSON.stringify(cachedValue(c)));
    else localStorage.removeItem(FILTER_KEY);
  } catch {
    /* storage unavailable: the filter lasts as long as the page */
  }
}

/** The server's filter: null when the user has never set one there, at its revision (0 then). */
export interface ServerFilter {
  filter: DashFilter | null;
  rev: number;
}

/** The filter among the server's preferences and their revisions. */
export function serverFilter(preferences: Record<string, unknown>, revisions: Record<string, unknown>): ServerFilter {
  const rev = count(revisions[FILTER_PREFERENCE]);
  // No value at a revision: it was removed there, which is a cleared filter, not one never set.
  if (!(FILTER_PREFERENCE in preferences)) return { filter: rev ? NO_FILTER : null, rev };
  return { filter: parseStored(preferences[FILTER_PREFERENCE]), rev };
}

/**
 * How long an unsent change counts as the user's latest word against a
 * revision written elsewhere since: within it the change is sent over
 * that revision; past it (a browser left closed with a change it never
 * sent) the server's wins.
 */
export const UNSENT_FRESH_MS = 5 * 60_000;

/**
 * The user's change made again on what another device wrote meanwhile:
 * `mine` was made from `base`, and the server now holds `theirs`. Theirs
 * is kept, with what the user added since `base` added and what they took
 * back taken back, facet by facet. So neither device's choices are lost,
 * and on a value both touched the user's word, the later, stands.
 */
export function rebase(base: DashFilter, mine: DashFilter, theirs: DashFilter): DashFilter {
  const facet = (f: Facet) => [...theirs[f].filter((v) => !base[f].includes(v) || mine[f].includes(v)), ...mine[f].filter((v) => !base[f].includes(v))];
  return normalize(each(facet));
}

/**
 * What a client's copy and the server's answer come to: the filter to
 * show, and whether to send it to the server.
 * - A copy with an unsent change shows and is sent when the server is
 *   still at the revision the change was made from, or the change is
 *   recent (`UNSENT_FRESH_MS`); otherwise the server's later revision wins.
 *   Sent onto a revision written since, it is made again on that
 *   revision's filter (`rebase`, from the copy's `base`), never over it.
 * - A server that never had a filter takes this client's, when it has any.
 * - Otherwise the server's shows, unless it is at an older revision than
 *   the copy: a client never goes back.
 */
export function reconcile(cached: CachedFilter, server: ServerFilter, now: number): { filter: DashFilter; send: boolean } {
  if (cached.unsent) {
    if (server.rev === cached.rev) return { filter: cached.filter, send: true };
    if (now - cached.at < UNSENT_FRESH_MS) return { filter: cached.base ? rebase(cached.base, cached.filter, server.filter ?? NO_FILTER) : cached.filter, send: true };
    return { filter: server.filter ?? NO_FILTER, send: false };
  }
  if (server.filter === null) return { filter: cached.filter, send: isActive(cached.filter) };
  if (server.rev < cached.rev) return { filter: cached.filter, send: false };
  return { filter: server.filter, send: false };
}

/**
 * What a dashboard URL shows, given the filter it names (`named`, null
 * when it names none) and the user's own (`mine`): the named filter, as a
 * `shared` view when it is not the user's own; the user's own otherwise.
 * A shared view is looked at, never stored.
 */
export function viewOf(named: DashFilter | null, mine: DashFilter): { filter: DashFilter; shared: boolean } {
  if (named === null || sameFilter(named, mine)) return { filter: mine, shared: false };
  return { filter: named, shared: true };
}

/** Whether two filters choose the same values. */
export function sameFilter(a: DashFilter, b: DashFilter): boolean {
  return FACETS.every((f) => a[f].length === b[f].length && a[f].every((v, i) => v === b[f][i]));
}
